import React from 'react';
import {
  ForgotPasswordPageProps,
  useRouterType,
  useLink,
  useRouterContext,
  useTranslation,
  useNavigation,
} from '@refinedev/core';
import {
  containerStyles,
  headStyles,
  bodyStyles,
  cardTitle,
  resetPasswordTitle,
  resetSubPasswordTitle,
} from './styles';
import {
  Row,
  Col,
  Layout,
  Card,
  Typography,
  Form,
  Input,
  Button,
  LayoutProps,
  CardProps,
  FormProps,
  theme,
  message,
} from 'antd';
import { AppIcon } from '../../../assets/icons';
import { colors } from '@/constants';
import { LanguageSwitcher } from '@/components/layout/changeLang';
import { motion } from 'framer-motion';
import { endPoints } from '../../api/configs';
import useAPI from '../../api/useApi';
import { setItem } from '@/core/storage';
import { useProfileStore } from '@/core';

type ResetPasswordProps = ForgotPasswordPageProps<
  LayoutProps,
  CardProps,
  FormProps
>;

/**
 * **refine** has forgot password page form which is served on `/forgot-password` route when the `authProvider` configuration is provided.
 *
 * @see {@link https://refine.dev/docs/ui-frameworks/antd/components/antd-auth-page/#forgot-password} for more details.
 */
export const ForgotPasswordPage: React.FC<ResetPasswordProps> = ({
  loginLink,
  wrapperProps,
  contentProps,
  formProps,
}) => {
  const { setForgotPasswordEmail } = useProfileStore();
  const { token } = theme.useToken();
  const [form] = Form.useForm<{ email: string }>();
  const routerType = useRouterType();
  const Link = useLink();
  const { Link: LegacyLink } = useRouterContext();
  const { translate } = useTranslation();
  const ActiveLink = routerType === 'legacy' ? LegacyLink : Link;
  const { usePost } = useAPI();
  const { push } = useNavigation();
  const { isLoading, mutateAsync: forgotPassword } = usePost<
    { accessToken: string },
    { email: string }
  >(endPoints.forgotPassword);
  const onSubmit = (values: { email: string }) => {
    forgotPassword(
      { email: values.email },
      {
        onSuccess: () => {
          setForgotPasswordEmail(values.email);
          setItem('forgotPasswordEmail', values.email);
          push('/verification');
        },
        onError: (error) => {
          form.resetFields();
          message.error(error.message);
        },
      }
    );
  };
  const CardTitle = (
    <div style={cardTitle}>
      <AppIcon />
      <div style={{ ...resetPasswordTitle }}>
        {translate('pages.forgotPassword.title')}
      </div>
      <div style={{ ...resetSubPasswordTitle }}>
        {translate('pages.forgotPassword.subTitle')}
      </div>
    </div>
  );
  const CardContent = (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card
        title={CardTitle}
        styles={{ header: headStyles, body: bodyStyles }}
        style={{
          ...containerStyles,
          backgroundColor: token.colorBgElevated,
        }}
        {...(contentProps ?? {})}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onSubmit}
            requiredMark={false}
            {...formProps}
          >
            <motion.div
              initial={{ x: -20 }}
              animate={{ x: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
            >
              <Form.Item
                name="email"
                label={translate('pages.forgotPassword.email')}
                rules={[
                  {
                    required: true,
                    message: translate('pages.errors.required'),
                  },
                  {
                    type: 'email',
                    message: translate('pages.login.errors.validEmail'),
                    whitespace: true,
                  },
                ]}
              >
                <Input
                  type="email"
                  size="large"
                  placeholder={translate('pages.forgotPassword.email')}
                />
              </Form.Item>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                {loginLink ?? (
                  <Typography.Text
                    style={{
                      fontSize: 12,
                      marginLeft: 'auto',
                    }}
                  >
                    {translate('pages.forgotPassword.haveAccount')}{' '}
                    <ActiveLink
                      style={{
                        fontWeight: 'bold',
                        color: colors.primary,
                      }}
                      to="/login"
                    >
                      {translate('pages.login.signin')}
                    </ActiveLink>
                  </Typography.Text>
                )}
              </div>
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.5 }}
            >
              <Form.Item
                style={{
                  marginTop: '24px',
                  marginBottom: 0,
                }}
              >
                <Button
                  type="primary"
                  size="large"
                  htmlType="submit"
                  block
                  style={{
                    backgroundColor: token.colorPrimary,
                  }}
                  loading={isLoading}
                >
                  {translate('pages.forgotPassword.send')}
                </Button>
              </Form.Item>
            </motion.div>
          </Form>
        </motion.div>
      </Card>
    </motion.div>
  );

  return (
    <Layout {...(wrapperProps ?? {})}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          position: 'absolute',
          top: '24px',
          right: '24px',
          zIndex: 999,
        }}
      >
        <LanguageSwitcher />
      </motion.div>

      <Row
        justify="center"
        align="middle"
        style={{
          padding: '16px 0',
          minHeight: '100dvh',
        }}
      >
        <Col xs={22}>{CardContent}</Col>
      </Row>
    </Layout>
  );
};
